<script lang="ts">
import { modalRegister } from '@/services/modals';
import PromptModal from './PromptModal.vue';
import type { ButtonVariants } from '../../../cross-app-frontend/button';

export async function promptModal(args: {
  title: string;
  message: string;
  placeholderText?: string;
  initialText?: string;
  choices?: { label: string; value: true | false; icon: string; variant: ButtonVariants['variant'] }[];
  type?: 'text' | 'textarea';
}): Promise<string | null> {
  return modalRegister({
    content: PromptModal,
    bindings: {
      choices: [
        { label: 'Cancel', value: false, icon: 'fa-xmark', variant: 'outline' },
        { label: 'Proceed', value: true, icon: 'fa-check', variant: 'default' },
      ],
      placeholderText: '',
      initialText: '',
      ...args,
      type: args.type ?? 'text',
    },
  });
}
</script>

<script setup lang="ts">
import BootstrapModal from '@/components/modals/BootstrapModal.vue';
import { Button } from '../../../cross-app-frontend/button';
import { Input } from '../../../cross-app-frontend/input';
import { Textarea } from '../../../cross-app-frontend/textarea';
import { ref, type Directive } from 'vue';
import { type ModalItem } from '@/services/modals';

const props = defineProps<{
  modalID: ModalItem['modalID'];
  title: string;
  message: string;
  placeholderText: string;
  initialText: string;
  choices: { label: string; value: any; icon: string; variant: ButtonVariants['variant'] }[];
  type: 'text' | 'textarea';
}>();

const text = ref(props.initialText);

const modal = ref<InstanceType<typeof BootstrapModal> | null>(null);
defineExpose({ modal });

const vFocus: Directive<HTMLElement, never> = {
  mounted(el) {
    el.focus();
  },
};
</script>

<template>
  <BootstrapModal ref="modal" :title="title" :modalID="modalID">
    <template #default="{ close }">
      <p class="mb-3 text-sm text-slate-700" style="white-space: pre-line">{{ message }}</p>

      <Input
        v-if="type === 'text'"
        type="text"
        v-model="text"
        v-focus
        :placeholder="placeholderText"
        @keydown.enter="close(text)"
      />
      <Textarea v-else v-focus :placeholder="placeholderText" v-model="text" rows="4"></Textarea>
    </template>

    <template #footer="{ close }">
      <Button
        v-for="(choice, index) of choices"
        :key="index"
        :variant="choice.variant"
        @click="close(choice.value === true ? text : null)"
      >
        <i class="fa-light" :class="choice.icon"></i>
        {{ choice.label }}
      </Button>
    </template>
  </BootstrapModal>
</template>
